import {Fragment, memo, useEffect, useMemo} from "react";
import {useQuery} from "react-query";
import {Route, useRouteMatch} from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";

import {fetcher, hasLoginError} from "../../functions/requestFetcher";

import Loading from "../../components/UI/Loading/Loading";
import Spinner from "../../components/UI/Loading/Spinner";

import MusicShelf from "../../components/MusicShelf/MusicShelf";

import {getTextFromArr, getTitleFromArr} from "../../functions/getJsxFromArr";
import {getKey} from "../../functions/uniqueSuffix";
import {getRegionalPreferences} from "../../functions/regionPreferences";

// Sub route component
import NewReleases from "./New_releases";

import {t} from "../../functions/i18n";
import {getKey as getContextKey} from "../../context/AuthContext";

// testing

const RENDERER = {counter: 0};

function Home({rootElementRef, token, logout, language, country, username, avatarUrl}) {
  const routeMatch = useRouteMatch();
  const regionalPreferences = getRegionalPreferences({language, country});
  // const history = useHistory();

  const {data: historyData, isSuccess: historyLoaded} = useQuery(
    ["Kiritofy", "history"],
    () => {
      return fetcher(
        {
          url: window.__SOUNDREX_API_URL__ + "/Kiritofy/history",
          method: "GET",
          headers: {
            authorization: "Bearer " + token,
          },
        },
        false,
        false,
        true,
        "Opps! something went wrong.",
        {
          autoClose: 1000,
          theme: "dark",
        }
      );
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!token,
    }
  );

  useEffect(() => {
    rootElementRef?.current &&
      rootElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, [rootElementRef]);

  // console.log("Loggin query data", client.getQueryCache().findAll(["song"]));

  const {data, error, isError, isSuccess} = useQuery(
    ["home", "charts", regionalPreferences.country, regionalPreferences.language],
    () => {
      return fetcher(
        {
          url: `${window.__SOUNDREX_API_URL__}/api/v1/home/browse/all?country=${regionalPreferences.country}&language=${regionalPreferences.language}`,
          method: "GET",
          headers: {
            authorization: "Bearer " + token,
          },
        },
        false,
        false,
        true,
        "Opps! something went wrong.",
        {
          autoClose: 1000,
          theme: "dark",
        }
        // () => logout()
      );
    },
    {
      staleTime: 180000, // 3min
      keepPreviousData: true,
      select: data => {
        if (data?.home?.length <= 0) throw new Error(t(language, "noDataFound"));
        for (let item of data.home) {
          const list = item?.hasAdditionalLists ? item?.list : [item];

          if (
            typeof item?.title === "string" &&
            item.title.toLowerCase().includes("genre")
          ) {
            list.forEach(i => {
              i.disableCarousel = true;
              i.underLine = true;
              i.hasMoreItems = false;
            });
          } else {
            list.forEach((element, i) => {
              if (RENDERER.counter === 0) {
                element.hasMoreItems = element.list.length > 10;
              }
              if (element?.title?.text?.toLowerCase()?.includes("artist")) {
                element.hasMoreItems = false;
              } else {
                // if current element is new_releases
                if (
                  !element?.title?.navigate &&
                  typeof item?.title === "string" &&
                  item.title.toLowerCase().includes("release")
                ) {
                  element.title = {
                    text: element?.title,
                    navigate: `${routeMatch.path}/new_releases`,
                  };
                }

                element.list.splice(10);
              }
            });
          }
        }

        RENDERER.counter++;
        return data;
      },
    }
  );

  let content = (
    <Fragment>
      <div className="fixed-top flex-center">
        <Loading />
      </div>

      <div className="fixed-right">
        <Spinner thin />
      </div>

      <Skeleton className={"skeleton-title skeleton-bg"} />
      <div className="skeleton-grid-list">
        <Skeleton className={"skeleton-grid-item skeleton-bg"} />

        <Skeleton
          animation="wave"
          className={"skeleton-grid-item skeleton-bg"}
        />
        <Skeleton
          animation={false}
          className={"skeleton-grid-item skeleton-bg"}
        />
      </div>

      <Skeleton className={"skeleton-title skeleton-bg mg-t-4"} />
      <div className="skeleton-grid-list">
        <Skeleton className={"skeleton-grid-item skeleton-bg"} />

        <Skeleton
          animation="wave"
          className={"skeleton-grid-item skeleton-bg"}
        />
        <Skeleton
          animation={false}
          className={"skeleton-grid-item skeleton-bg"}
        />
      </div>

      <Skeleton className={"skeleton-title skeleton-bg mg-t-4"} />
      <div className="skeleton-grid-list">
        <Skeleton className={"skeleton-grid-item skeleton-bg"} />

        <Skeleton
          animation="wave"
          className={"skeleton-grid-item skeleton-bg"}
        />
        <Skeleton
          animation={false}
          className={"skeleton-grid-item skeleton-bg"}
        />
      </div>

      {/* <div className="flex-center mg-t-3">
        <Spinner />
      </div> */}
    </Fragment>
  );

  // if there is an error, immediately return it to Ui
  if (isError && error) {
    const hasLoginErr = hasLoginError(error);
    if (hasLoginErr) {
      logout();
    }
    content = <p className="centered capitalize text-medium">{t(language, "noDataFound")}</p>;
  }

  const formatHistoryToList = useMemo(() => {
    return (history) => {
      return history?.map(item => {
        let thumbUrl = item.thumbnail || "";
        
        if (thumbUrl && thumbUrl.includes("googleusercontent.com")) {
          thumbUrl = thumbUrl.replace(/=w\d+-h\d+/g, '=w800-h800');
          thumbUrl = thumbUrl.replace(/=w\d+/g, '=w800');
        }
        
        return {
          videoId: item.videoId,
          browseId: item.videoId,
          title: [{ text: item.title || "Unknown" }],
          subtitle: (item.artist && item.artist.trim()) 
            ? [{ text: item.artist }] 
            : [{ text: t(language, "unknownArtist") }],
          thumbnails: thumbUrl 
            ? [
                { url: thumbUrl, width: 400, height: 400 },
                { url: thumbUrl, width: 800, height: 800 },
                { url: thumbUrl, width: 1200, height: 1200 }
              ] 
            : [{ url: "/icons/maskable_icon_x192.png", width: 192, height: 192 }],
          menu: [],
        };
      }) || [];
    };
  }, []);

  const historyList = historyLoaded && historyData?.history?.length > 0 
    ? formatHistoryToList(historyData.history.slice(0, 10))
    : [];

  const listenAgainSection = historyLoaded && historyList.length > 0 && (
    <div key="listen-again-section" className="listen-again-section">
      <div className="listen-again-header">
        <div className="listen-again-profile">
          <Avatar 
            src={avatarUrl || undefined}
            sx={{width: 56, height: 56}}
            className="listen-again-avatar"
          >
            {username?.split("")?.[0]?.toUpperCase() || "U"}
          </Avatar>
          <div className="listen-again-info">
            <span className="listen-again-username">{username}</span>
            <h2 className="listen-again-title">{t(language, "listenAgain") || "Ouvir de novo"}</h2>
          </div>
        </div>
      </div>
      <MusicShelf
        list={historyList}
        disableCarousel={false}
        hasMoreItems={historyData?.history?.length > 10}
        isListenAgain={true}
      />
    </div>
  );

  if (isSuccess) {
    if (data) {
      content = [
        ...(listenAgainSection ? [listenAgainSection] : [])
      ];

      for (let item of data.home) {
        const list = item?.hasAdditionalLists ? item?.list : [item];
        const listTitle = item?.title;

        let endpointType = "/browse";
        if (
          typeof listTitle === "string" &&
          listTitle.toLowerCase().includes("genre")
        )
          endpointType = "/genres";

        let index = 0;
        for (let el of list) {
          const title = el?.title;

          let newTitle = title;
          if (title?.text) newTitle = title?.text;
          if (typeof title !== "string" && title?.length > 0)
            newTitle = getTitleFromArr(title);

          let link = `${endpointType}/${title?.navigate?.browseId}`;
          // if (typeof title?.navigate === "string")
          if (typeof title?.navigate === "string") link = title?.navigate;

          const strapline = el?.strapline && getTextFromArr(el.strapline);

          content.push(
            <MusicShelf
              key={getKey(title?.text + index + (title?.navigate?.browseId || ''))}
              endpointType={endpointType}
              isRectangleBox={el?.isRectangleBox}
              rectangleBoxClass={el?.isRectangleBox && "genre-box-p"}
              disableCarousel={el?.disableCarousel}
              underLine={el?.underLine}
              hasMoreItems={el?.hasMoreItems}
              list={el?.list}
              title={
                newTitle.includes("_")
                  ? newTitle.split("_").join(" ")
                  : newTitle
              }
              strapline={strapline}
              navigate={link}
            />
          );
          index++;
        }
      }
    } else {
      content = (
        <p className="centered capitalize text-medium">{t(language, "noDataFound")}</p>
      );
    }
  }

  return (
    <Fragment>
      <Route path={routeMatch.path} exact>
        {content}
      </Route>

      {/* /home/new_releases route */}
      <Route path={`${routeMatch.path}/new_releases`}>
        <NewReleases
          logout={logout}
          token={token}
          language={regionalPreferences.language}
          country={regionalPreferences.country}
        />
      </Route>
    </Fragment>
  );
}

export default memo(Home);

